import {
  DATE_PRESET_LABELS,
  DATE_PRESETS,
  type DatePreset,
  type DateRange,
  isValidYmd,
  resolveDatePreset,
  shiftDateRange,
  todayInTimezone,
  type WeekStart,
  type YearMode,
} from '@mm/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { formatDate } from '../utils/format';
import { Input, Select } from './ui/inputs';

export interface DateFilterState extends DateRange {
  preset: DatePreset;
}

/**
 * Date filter state kept in the URL (?preset=thisMonth or ?preset=custom&start=…&end=…)
 * so filtered views can be bookmarked and shared. Default: This Month.
 */
export function useDateFilter(
  defaultPreset: Exclude<DatePreset, 'custom'> = 'thisMonth',
  opts: { weekStartsOn?: WeekStart; yearMode?: YearMode } = {},
): [DateFilterState, (next: Partial<DateFilterState>) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('preset') as DatePreset | null;
  const preset: DatePreset =
    raw && (DATE_PRESETS as readonly string[]).includes(raw) ? raw : defaultPreset;
  const today = todayInTimezone();

  let range: DateRange;
  if (preset === 'custom') {
    const start = params.get('start') ?? '';
    const end = params.get('end') ?? '';
    range =
      isValidYmd(start) && isValidYmd(end) && start <= end
        ? { startDate: start, endDate: end }
        : resolveDatePreset(defaultPreset, { today, ...opts });
  } else {
    range = resolveDatePreset(preset, { today, ...opts });
  }

  const update = (next: Partial<DateFilterState>) => {
    const p = new URLSearchParams(params);
    const nextPreset = next.preset ?? preset;
    p.set('preset', nextPreset);
    if (nextPreset === 'custom') {
      p.set('start', next.startDate ?? range.startDate);
      p.set('end', next.endDate ?? range.endDate);
    } else {
      p.delete('start');
      p.delete('end');
    }
    setParams(p, { replace: true });
  };

  return [{ preset, ...range }, update];
}

const ARROW =
  'inline-flex size-10 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 shadow-sm hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40';

const rangeText = (r: DateRange & { preset: DatePreset }) =>
  r.preset !== 'custom'
    ? DATE_PRESET_LABELS[r.preset]
    : r.startDate === r.endDate
      ? formatDate(r.startDate)
      : `${formatDate(r.startDate)} to ${formatDate(r.endDate)}`;

/** Preset or custom range, with ‹ › to step to the previous / next day, week, month or year. */
export function DateRangeFilter({
  value,
  onChange,
}: {
  value: DateFilterState;
  onChange: (next: Partial<DateFilterState>) => void;
}) {
  const today = todayInTimezone();
  const prev = shiftDateRange(value.preset, value, -1, { today });
  const next = shiftDateRange(value.preset, value, 1, { today });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          className={ARROW}
          aria-label="Previous period"
          title={`Previous: ${rangeText(prev)}`}
          onClick={() => onChange(prev)}
        >
          <ChevronLeft className="size-4" />
        </button>
        <div className="w-40">
          <Select
            aria-label="Date range"
            value={value.preset}
            onChange={(e) => onChange({ preset: e.target.value as DatePreset })}
          >
            {DATE_PRESETS.map((p) => (
              <option key={p} value={p}>
                {DATE_PRESET_LABELS[p]}
              </option>
            ))}
          </Select>
        </div>
        <button
          type="button"
          className={ARROW}
          aria-label="Next period"
          title={next.startDate > today ? 'No later period' : `Next: ${rangeText(next)}`}
          disabled={next.startDate > today}
          onClick={() => onChange(next)}
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
      {value.preset === 'custom' && (
        <div className="flex shrink-0 items-center gap-2">
          <div className="w-40">
            <Input
              type="date"
              aria-label="Start date"
              value={value.startDate}
              max={value.endDate}
              onChange={(e) => e.target.value && onChange({ startDate: e.target.value })}
            />
          </div>
          <span className="text-sm text-slate-400">to</span>
          <div className="w-40">
            <Input
              type="date"
              aria-label="End date"
              value={value.endDate}
              min={value.startDate}
              onChange={(e) => e.target.value && onChange({ endDate: e.target.value })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
